/**
 * Helper components for MultiActionTimeline.
 * Extracted to keep the main component file within the 300-line guideline.
 * Pure utility functions live in multiActionTimeline.utils.ts.
 */

import { useCallback, useMemo } from 'react';

import { useAssociationTypes } from '../../lib/crm/hooks/useAssociationTypes';
import { usePropertySchema } from '../../lib/crm/hooks/usePropertySchema';
import { getEntityTypeConfig } from '../../lib/crm/record/registry/entityTypeRegistry';
import {
  DISPLAY_NAME_FIELDS,
  SYSTEM_FIELDS,
} from '../../lib/crm/record/registry/fieldConstants';
import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import type { SelectionState } from '../../lib/proposals/types/selection';
import {
  getEntityTypeLabel,
  type TypedActionGroup,
} from '../../lib/proposals/utils/actionGrouping';
import { isSameLogicalAction } from '../../lib/proposals/utils/helpers';
import { ActionTable } from './ActionTable';
import type { ColumnDef } from './ActionTable.types';
import ActionVersionTimeline from './ActionVersionTimeline';
import { mergeActionUpdate } from './multiActionTimeline.utils';

// ============================================================================
// ActionTableWithSchema
// ============================================================================

const MAX_TABLE_COLUMNS = 6;
const TIMESTAMP_FIELDS = new Set(['ahoy_created_at', 'ahoy_updated_at']);

export interface ActionTableWithSchemaProps {
  group: TypedActionGroup;
  orgId: number;
  proposal: ProposalDetail;
  onUpdate: (proposal: ProposalDetail) => void;
  isProcessing: boolean;
  selectionState?: SelectionState;
  fillContainer?: boolean;
}

/**
 * Wrapper component for ActionTable that fetches property schema.
 * This exists as a separate component so we can call usePropertySchema hook.
 */
export function ActionTableWithSchema({
  group,
  orgId,
  proposal,
  onUpdate,
  isProcessing,
  selectionState,
  fillContainer = false,
}: ActionTableWithSchemaProps) {
  // Fetch property schema + association types for this entity type.
  // Association types supply human-readable labels for association columns
  // (e.g. `ahoy_contact_to_company` → "Works at / Company") — the equivalent
  // of what usePropertySchema gives us for property columns.
  const { schema } = usePropertySchema(orgId, group.entityTypeName ?? '');
  const { associationTypes } = useAssociationTypes(orgId);

  // Build combined column labels map:
  //   property columns → usePropertySchema label
  //   association columns → useAssociationTypes label
  const columnLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const [key, propSchema] of Object.entries(schema)) {
      labels[key] = propSchema.label;
    }
    for (const assoc of associationTypes) {
      labels[assoc.association_type_name] = assoc.label;
    }
    return labels;
  }, [schema, associationTypes]);

  // Determine effective columns (property + association) to display as table
  // columns. Caps at MAX_TABLE_COLUMNS total, uses detailFieldOrder when
  // available, applies entity-type-specific exclusions.
  //
  // detailFieldOrder in entityTypeRegistry mixes property, association, and
  // timestamp entries in the order they should surface in the slideout. We
  // keep property + association entries (skipping timestamps) so the table
  // mirrors the slideout ordering — e.g. ahoy_contact naturally surfaces
  // first_name, last_name, email, phone_number, job_title, contact_to_company
  // as the first six columns, which is what "Works at" lives on.
  const effectiveColumns = useMemo<ColumnDef[]>(() => {
    const config = getEntityTypeConfig(group.entityTypeName ?? '');
    const exclusionSet = new Set([
      ...(config.hiddenProperties ?? []),
      ...TIMESTAMP_FIELDS,
    ]);

    if (group.propertyKeys && group.propertyKeys.length > 0) {
      // Path A: create/update groups. Only property columns are meaningful
      // here — the action payload carries property values but not
      // associations, and rows don't read from the CRM store, so any
      // association column would render "—" for every row. Including them
      // would just displace real payload fields below the MAX_TABLE_COLUMNS
      // cap.
      if (config.detailFieldOrder) {
        const payloadKeySet = new Set(group.propertyKeys);
        const orderedKeySet = new Set<string>();
        const ordered = config.detailFieldOrder
          .filter(
            f =>
              f.type === 'property' &&
              !exclusionSet.has(f.key) &&
              payloadKeySet.has(f.key)
          )
          .map<ColumnDef>(f => {
            orderedKeySet.add(f.key);
            return { key: f.key, kind: 'property' };
          });
        // Append payload fields not listed in detailFieldOrder so custom /
        // unregistered properties still appear in the table (the ordered list
        // is a priority guide, not an allowlist).
        const extra = group.propertyKeys
          .filter(
            key =>
              !orderedKeySet.has(key) &&
              !exclusionSet.has(key) &&
              !SYSTEM_FIELDS.has(key) &&
              !DISPLAY_NAME_FIELDS.has(key)
          )
          .map<ColumnDef>(key => ({ key, kind: 'property' }));
        return [...ordered, ...extra].slice(0, MAX_TABLE_COLUMNS);
      }
      // Fallback: filter payload keys by system/timestamp exclusions. Keep
      // display-name fields here — for entity types without registry
      // ordering, the display name may be the only visible column and
      // dropping it would leave proposal rows effectively blank. No
      // association column support in the fallback — those only come from
      // detailFieldOrder.
      return group.propertyKeys
        .filter(key => !SYSTEM_FIELDS.has(key) && !exclusionSet.has(key))
        .map<ColumnDef>(key => ({ key, kind: 'property' }))
        .slice(0, MAX_TABLE_COLUMNS);
    }

    // Path C: no propertyKeys — nothing to derive columns from.
    return [];
  }, [group.propertyKeys, group.entityTypeName]);

  // Derive human-readable entity type label (e.g., 'contacts', 'companies')
  const entityTypeLabel = group.entityTypeName
    ? getEntityTypeLabel(group.entityTypeName)
    : undefined;

  return (
    <ActionTable
      actions={group.actions}
      columns={effectiveColumns}
      onUpdate={onUpdate}
      isProcessing={isProcessing}
      selectionState={selectionState}
      proposal={proposal}
      entityTypeLabel={entityTypeLabel}
      columnLabels={columnLabels}
      propertySchema={schema}
      fillContainer={fillContainer}
    />
  );
}

// ============================================================================
// AccordionActionContent
// ============================================================================

type ActionUpdater = (
  actionUpdater: (action: ProposedAction) => ProposedAction | null
) => void;

/**
 * Shared rendering of ActionVersionTimeline inside an accordion item.
 * Extracted to avoid duplicating the update-merge logic.
 */
export function AccordionActionContent({
  action,
  proposal,
  onUpdate,
  onActionUpdate,
  isProcessing,
  onOpenThread,
  optimisticMessage,
  isSoleAction = false,
}: {
  action: ProposedAction;
  proposal: ProposalDetail;
  onUpdate: (proposal: ProposalDetail) => void;
  onActionUpdate?: ActionUpdater;
  isProcessing: boolean;
  onOpenThread?: () => void;
  optimisticMessage?: string | null;
  isSoleAction?: boolean;
}) {
  const handleVersionTimelineUpdate = useCallback(
    (updatedSingleActionProposal: ProposalDetail) => {
      const updatedAction = updatedSingleActionProposal.actions[0];
      if (!updatedAction) {
        console.error('[MultiActionTimeline] No action in updated proposal');
        return;
      }
      if (onActionUpdate) {
        onActionUpdate(existingAction =>
          isSameLogicalAction(existingAction, updatedAction)
            ? updatedAction
            : null
        );
      } else {
        onUpdate(mergeActionUpdate(proposal, updatedAction));
      }
    },
    [onUpdate, onActionUpdate, proposal]
  );

  return (
    <ActionVersionTimeline
      action={{
        ...proposal,
        actions: [action],
      }}
      onUpdate={handleVersionTimelineUpdate}
      onActionUpdate={onActionUpdate}
      isProcessing={isProcessing}
      onOpenThread={onOpenThread}
      optimisticMessage={optimisticMessage}
      isSoleAction={isSoleAction}
    />
  );
}
